export type TemplateField = {
  id: string;
  label: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
};

export type TemplateMetadata = Record<string, string | number | boolean>;

export interface TemplateDefinition {
  id: string;
  name: string;
  description: string;
  defaultContent: string;
  defaultFields?: TemplateField[];
  defaultMetadata?: TemplateMetadata;
}

export const templates = [
  {
    id: "meeting-agenda",
    name: "Meeting Agenda",
    description: "Organize talking points, owners, and outcomes for your next meeting.",
    defaultContent: `# Meeting Agenda: {meetingTitle}

## Objective
- Clarify the purpose and expected outcomes of the meeting

## Attendees
- {attendees}

## Agenda
1. {topicOne} (Owner: {ownerOne})
2. {topicTwo} (Owner: {ownerTwo})

## Decisions
- Capture key decisions made during the meeting

## Action Items
- [ ] {actionItemOne} (Owner: {actionOwnerOne}, Due: {actionDueOne})
- [ ] {actionItemTwo} (Owner: {actionOwnerTwo}, Due: {actionDueTwo})
`,
    defaultFields: [
      {
        id: "meetingTitle",
        label: "Meeting title",
        placeholder: "Q2 Planning Sync",
        defaultValue: "Q2 Planning Sync",
      },
      {
        id: "attendees",
        label: "Attendees",
        placeholder: "Product, Design, Marketing",
        defaultValue: "Product, Design, Marketing",
      },
      { id: "topicOne", label: "Topic 1", placeholder: "Roadmap updates", defaultValue: "Roadmap updates" },
      { id: "ownerOne", label: "Owner 1", placeholder: "Alex", defaultValue: "Alex" },
      { id: "topicTwo", label: "Topic 2", placeholder: "Budget alignment", defaultValue: "Budget alignment" },
      { id: "ownerTwo", label: "Owner 2", placeholder: "Sam", defaultValue: "Sam" },
      {
        id: "actionItemOne",
        label: "Action item 1",
        placeholder: "Share updated deck",
        defaultValue: "Share updated deck",
      },
      { id: "actionOwnerOne", label: "Action owner 1", placeholder: "Priya", defaultValue: "Priya" },
      { id: "actionDueOne", label: "Action due 1", placeholder: "Friday", defaultValue: "Friday" },
      {
        id: "actionItemTwo",
        label: "Action item 2",
        placeholder: "Finalize budget",
        defaultValue: "Finalize budget",
      },
      { id: "actionOwnerTwo", label: "Action owner 2", placeholder: "Jordan", defaultValue: "Jordan" },
      {
        id: "actionDueTwo",
        label: "Action due 2",
        placeholder: "Next Monday",
        defaultValue: "Next Monday",
      },
    ],
    defaultMetadata: {
      category: "meeting",
      recommendedDurationMinutes: 45,
    },
  },
  {
    id: "webinar-script",
    name: "Webinar Script",
    description: "Guide hosts through introductions, key talking points, and CTAs for a live webinar.",
    defaultContent: `# Webinar Script: {sessionTitle}

## Opening
- Welcome attendees and set expectations
- Introduce speakers: {speakerNames}

## Agenda
1. {sectionOneTitle}\n   - {sectionOneDetails}
2. {sectionTwoTitle}\n   - {sectionTwoDetails}
3. {sectionThreeTitle}\n   - {sectionThreeDetails}

## Demo Notes
- Highlight the main features to show
- Capture questions for follow-up

## Call to Action
- Primary CTA: {primaryCTA}
- Secondary CTA: {secondaryCTA}

## Closing
- Thank attendees and share next steps or resources
`,
    defaultFields: [
      {
        id: "sessionTitle",
        label: "Session title",
        placeholder: "How to ship faster with AI",
        defaultValue: "How to ship faster with AI",
      },
      {
        id: "speakerNames",
        label: "Speakers",
        placeholder: "Taylor (PM), Casey (Engineer)",
        defaultValue: "Taylor (PM), Casey (Engineer)",
      },
      {
        id: "sectionOneTitle",
        label: "Section 1 title",
        placeholder: "Problem overview",
        defaultValue: "Problem overview",
      },
      {
        id: "sectionOneDetails",
        label: "Section 1 details",
        placeholder: "Key stats, quotes, or story",
        defaultValue: "Key stats, quotes, or story",
      },
      {
        id: "sectionTwoTitle",
        label: "Section 2 title",
        placeholder: "Solution walkthrough",
        defaultValue: "Solution walkthrough",
      },
      {
        id: "sectionTwoDetails",
        label: "Section 2 details",
        placeholder: "Live demo beats",
        defaultValue: "Live demo beats",
      },
      {
        id: "sectionThreeTitle",
        label: "Section 3 title",
        placeholder: "Customer proof",
        defaultValue: "Customer proof",
      },
      {
        id: "sectionThreeDetails",
        label: "Section 3 details",
        placeholder: "Testimonials or ROI",
        defaultValue: "Testimonials or ROI",
      },
      { id: "primaryCTA", label: "Primary CTA", placeholder: "Start free trial", defaultValue: "Start free trial" },
      {
        id: "secondaryCTA",
        label: "Secondary CTA",
        placeholder: "Book a follow-up call",
        defaultValue: "Book a follow-up call",
      },
    ],
    defaultMetadata: {
      category: "event",
      targetAudience: "prospects",
    },
  },
  {
    id: "project-update",
    name: "Project Update",
    description: "Summarize project progress, risks, and next steps for stakeholders.",
    defaultContent: `# Project Update: {projectName}

## Status
- Overall health: {statusColor}
- Current sprint goals: {sprintGoals}

## Highlights
- {highlightOne}
- {highlightTwo}

## Risks & Blockers
- {riskOne}
- {riskTwo}

## Next Steps
1. {nextStepOne}
2. {nextStepTwo}

## Requests
- {requestOne}
`,
    defaultFields: [
      { id: "projectName", label: "Project name", placeholder: "Live Flow", defaultValue: "Live Flow" },
      {
        id: "statusColor",
        label: "Status",
        placeholder: "On Track / At Risk / Blocked",
        defaultValue: "On Track",
      },
      {
        id: "sprintGoals",
        label: "Sprint goals",
        placeholder: "Ship onboarding improvements",
        defaultValue: "Ship onboarding improvements",
      },
      { id: "highlightOne", label: "Highlight 1", placeholder: "Shipped new dashboard", defaultValue: "Shipped new dashboard" },
      {
        id: "highlightTwo",
        label: "Highlight 2",
        placeholder: "Reduced error rate by 20%",
        defaultValue: "Reduced error rate by 20%",
      },
      {
        id: "riskOne",
        label: "Risk 1",
        placeholder: "Dependency on API team",
        defaultValue: "Dependency on API team",
      },
      {
        id: "riskTwo",
        label: "Risk 2",
        placeholder: "Pending security review",
        defaultValue: "Pending security review",
      },
      { id: "nextStepOne", label: "Next step 1", placeholder: "Finish mobile QA", defaultValue: "Finish mobile QA" },
      { id: "nextStepTwo", label: "Next step 2", placeholder: "Enable feature flag", defaultValue: "Enable feature flag" },
      { id: "requestOne", label: "Requests", placeholder: "Need analytics access", defaultValue: "Need analytics access" },
    ],
    defaultMetadata: {
      category: "status",
      cadence: "weekly",
    },
  },
  {
    id: "customer-discovery",
    name: "Customer Discovery Call",
    description: "Frame customer calls with hypothesis, questions, and next steps.",
    defaultContent: `# Customer Discovery: {customerName}

## Context
- Company: {company}
- Persona: {persona}
- Goal for call: {callGoal}

## Hypothesis
- {hypothesis}

## Questions
1. {questionOne}
2. {questionTwo}
3. {questionThree}

## Observations
- Notes during the call

## Follow-ups
- [ ] {followUpOne}
- [ ] {followUpTwo}
`,
    defaultFields: [
      { id: "customerName", label: "Customer name", placeholder: "Jamie", defaultValue: "Jamie" },
      { id: "company", label: "Company", placeholder: "Acme Corp", defaultValue: "Acme Corp" },
      { id: "persona", label: "Persona", placeholder: "Ops Manager", defaultValue: "Ops Manager" },
      {
        id: "callGoal",
        label: "Call goal",
        placeholder: "Validate pain points",
        defaultValue: "Validate pain points",
      },
      {
        id: "hypothesis",
        label: "Hypothesis",
        placeholder: "Teams struggle to summarize meetings",
        defaultValue: "Teams struggle to summarize meetings",
      },
      {
        id: "questionOne",
        label: "Question 1",
        placeholder: "What triggers the need for this workflow?",
        defaultValue: "What triggers the need for this workflow?",
      },
      {
        id: "questionTwo",
        label: "Question 2",
        placeholder: "How do you measure success today?",
        defaultValue: "How do you measure success today?",
      },
      {
        id: "questionThree",
        label: "Question 3",
        placeholder: "What integrations are must-haves?",
        defaultValue: "What integrations are must-haves?",
      },
      { id: "followUpOne", label: "Follow-up 1", placeholder: "Send recap email", defaultValue: "Send recap email" },
      { id: "followUpTwo", label: "Follow-up 2", placeholder: "Schedule pilot", defaultValue: "Schedule pilot" },
    ],
    defaultMetadata: {
      category: "research",
      stage: "discovery",
    },
  },
  {
    id: "product-spec",
    name: "Product Spec",
    description: "Capture the problem statement, goals, scope, and acceptance criteria for a new feature.",
    defaultContent: `# Product Spec: {featureName}

## Problem Statement
- {problemStatement}

## Goals
- {primaryGoal}
- {secondaryGoal}

## Non-Goals
- {nonGoals}

## User Stories
- As a {persona}, I want {userNeed} so that {benefit}.

## Acceptance Criteria
- {acceptanceCriteria}

## Rollout
- Launch plan: {launchPlan}
- Metrics to watch: {metrics}
`,
    defaultFields: [
      { id: "featureName", label: "Feature name", placeholder: "Unified search", defaultValue: "Unified search" },
      {
        id: "problemStatement",
        label: "Problem statement",
        placeholder: "Users can't find saved notes",
        defaultValue: "Users can't find saved notes",
      },
      { id: "primaryGoal", label: "Primary goal", placeholder: "Improve activation", defaultValue: "Improve activation" },
      {
        id: "secondaryGoal",
        label: "Secondary goal",
        placeholder: "Reduce support tickets",
        defaultValue: "Reduce support tickets",
      },
      { id: "nonGoals", label: "Non-goals", placeholder: "Full offline support", defaultValue: "Full offline support" },
      { id: "persona", label: "Persona", placeholder: "Team lead", defaultValue: "Team lead" },
      {
        id: "userNeed",
        label: "User need",
        placeholder: "Search across meeting recaps",
        defaultValue: "Search across meeting recaps",
      },
      { id: "benefit", label: "Benefit", placeholder: "Plan projects faster", defaultValue: "Plan projects faster" },
      {
        id: "acceptanceCriteria",
        label: "Acceptance criteria",
        placeholder: "Search results show within 1s",
        defaultValue: "Search results show within 1s",
      },
      { id: "launchPlan", label: "Launch plan", placeholder: "Beta with 10 teams", defaultValue: "Beta with 10 teams" },
      {
        id: "metrics",
        label: "Metrics",
        placeholder: "Search success rate, retention",
        defaultValue: "Search success rate, retention",
      },
    ],
    defaultMetadata: {
      category: "product",
      status: "draft",
    },
  },
] satisfies TemplateDefinition[];

export function getTemplates(): TemplateDefinition[] {
  return templates;
}

export function getTemplateById(id: TemplateDefinition["id"]): TemplateDefinition | undefined {
  return templates.find((template) => template.id === id);
}

export function getDefaultTemplate(): TemplateDefinition {
  return templates[0];
}

export function getTemplateFields(
  id: TemplateDefinition["id"]
): TemplateField[] {
  return getTemplateById(id)?.defaultFields ?? [];
}

export function getTemplateMetadata(
  id: TemplateDefinition["id"]
): TemplateMetadata {
  return getTemplateById(id)?.defaultMetadata ?? {};
}

export function getTemplateFieldDefaults(id: TemplateDefinition["id"]): Record<string, string> {
  return getTemplateFields(id).reduce<Record<string, string>>((acc, field) => {
    acc[field.id] = field.defaultValue ?? "";
    return acc;
  }, {});
}

export function getTemplateDefaultMetadata(id: TemplateDefinition["id"]): TemplateMetadata {
  return getTemplateMetadata(id);
}
